import {
  autoUpdate,
  FloatingOverlay,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { IcClose } from "@jetblue/kit-icons/ic-close";
import { ExpandableIndicator } from "@jetblue/kit/expandable-indicator";
import { Incrementer } from "@jetblue/kit/incrementer";
import { Button } from "react-aria-components";
import type { PassengersControl } from "../../use-passengers";
import { MAX_CHILDREN_WITHOUT_ADULT } from "../../use-passengers";
import { useState } from "react";
import { createTravelersDescription } from "./label";
import type { FlightSearchFormLabels } from "../../labels";

export type PassengersInputMobileProperties = {
  controlAdults: PassengersControl;
  controlChildren: PassengersControl;
  controlInfants: PassengersControl;
  labels: FlightSearchFormLabels["passengersDropdown"];
  passengersButtonLabel: string;
};
export const PassengersInputMobile = ({
  controlAdults,
  controlChildren,
  controlInfants,
  labels,
  passengersButtonLabel,
}: PassengersInputMobileProperties) => {
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const { refs, context, floatingStyles } = useFloating({
    onOpenChange: (nextOpen) => {
      setIsFullScreenOpen(nextOpen);
    },
    open: isFullScreenOpen,
    placement: "top-start",
    strategy: "absolute",
    whileElementsMounted: autoUpdate,
  });

  useInteractions([
    useRole(context),
    useDismiss(context, {
      outsidePress: () => false,
    }),
  ]);

  const adultLabel = " " + labels.adult.toLowerCase();
  const childLabel = " " + labels.child.toLowerCase();
  const infantLabel = " " + labels.infant.toLowerCase();
  const travelersDescription = createTravelersDescription({
    adultsCount: controlAdults.value,
    childrenCount: controlChildren.value,
    infantsCount: controlInfants.value,
    labels,
  });
  return (
    <>
      <Button
        className="
          group/expandable-ui flex items-center gap-2 text-label font-extrabold!
          text-foreground-primary focus-outline

          disabled:cursor-not-allowed disabled:opacity-50
        "
        onClick={() => {
          setIsFullScreenOpen(true);
        }}
      >
        {passengersButtonLabel}
        <ExpandableIndicator />
      </Button>
      {isFullScreenOpen && (
        <FloatingOverlay
          lockScroll
          style={{
            height: "100%",
            width: "100%",
            zIndex: 30,
          }}
        >
          <div
            className="
              flex size-full flex-col justify-between
              bg-background-surface-container
            "
            data-fs-element="traveler-selector"
            // eslint-disable-next-line @typescript-eslint/unbound-method -- We want to use this ref.setFloating method to set the floating element reference.
            ref={refs.setFloating}
            style={{ ...floatingStyles }}
          >
            <div className="grow overflow-y-auto">
              <header
                className="
                  flex justify-center bg-background-primary-variant py-5
                  text-title-s text-foreground-on-primary
                "
              >
                <h4 className="">{labels.travelers}</h4>
                <button
                  className="fixed inset-e-2 top-2 p-5"
                  onClick={() => {
                    setIsFullScreenOpen(false);
                  }}
                >
                  <IcClose />
                </button>
              </header>
              <div className="p-4">
                <div
                  className="
                    my-6 flex flex-col gap-1 border-b border-border-variant-low
                    pb-6
                  "
                >
                  <span className="text-foreground-on-surface-variant-medium">
                    {labels.travelers}
                  </span>
                  <p className="text-foreground-primary">
                    {travelersDescription}
                  </p>
                </div>
                <div
                  className="
                    flex justify-between border-b border-border-variant-low pe-4
                    pb-4
                  "
                >
                  <div className="me-4 min-w-40">
                    {labels.adultsRestrictions}
                  </div>
                  <Incrementer
                    decrement={controlAdults.decrement}
                    decrementLabel={labels.decrement + adultLabel}
                    increment={controlAdults.increment}
                    incrementLabel={labels.increment + adultLabel}
                    isDecrementDisabled={controlAdults.isDecrementDisabled}
                    isIncrementDisabled={controlAdults.isIncrementDisabled}
                    name="adults"
                    value={controlAdults.value}
                  />
                </div>
                <div
                  className="
                    mt-4 flex justify-between border-b border-border-variant-low
                    pe-4 pb-4
                  "
                >
                  <div className="me-4 min-w-40">
                    {labels.childrenRestrictions}
                  </div>
                  <Incrementer
                    decrement={controlChildren.decrement}
                    decrementLabel={labels.decrement + childLabel}
                    increment={controlChildren.increment}
                    incrementLabel={labels.increment + childLabel}
                    isDecrementDisabled={controlChildren.isDecrementDisabled}
                    isIncrementDisabled={controlChildren.isIncrementDisabled}
                    name="children"
                    value={controlChildren.value}
                  />
                </div>
                <div
                  className="
                    mt-4 flex justify-between border-b border-border-variant-low
                    pe-4 pb-4
                  "
                >
                  <div className="me-4 min-w-40">
                    {labels.infantsRestrictions}
                  </div>
                  <Incrementer
                    decrement={controlInfants.decrement}
                    decrementLabel={labels.decrement + infantLabel}
                    increment={controlInfants.increment}
                    incrementLabel={labels.increment + infantLabel}
                    isDecrementDisabled={controlInfants.isDecrementDisabled}
                    isIncrementDisabled={controlInfants.isIncrementDisabled}
                    name="infants"
                    value={controlInfants.value}
                  />
                </div>
                <div
                  className="
                    mt-4 justify-between pb-4 text-foreground-on-surface
                  "
                >
                  <p
                    className="
                      me-4 min-w-40 text-caption

                      data-[emphasize-rule=true]:font-bold
                    "
                    data-emphasize-rule={
                      controlAdults.isIncrementDisabled &&
                      controlChildren.isIncrementDisabled
                    }
                  >
                    {labels.maximumTravelers}
                  </p>

                  <p
                    className="
                      me-4 min-w-40 text-caption

                      data-[emphasize-rule=true]:font-bold
                    "
                    data-emphasize-rule={
                      controlChildren.value > 0 &&
                      controlAdults.value === 0 &&
                      controlChildren.value === MAX_CHILDREN_WITHOUT_ADULT
                    }
                  >
                    {labels.maximumChildren}
                  </p>

                  <p
                    className="
                      me-4 min-w-40 text-caption

                      data-[emphasize-rule=true]:font-bold
                    "
                    data-emphasize-rule={controlInfants.isIncrementDisabled}
                  >
                    {labels.maximumInfants}
                  </p>

                  <div
                    className="
                      me-4 mt-3 min-w-40 text-caption

                      [&_a]:text-foreground-primary [&_a]:focus-outline

                      [&_a:hover]:text-foreground-primary-variant
                      [&_a:hover]:underline
                    "
                    dangerouslySetInnerHTML={{
                      __html: labels.specialAssistance,
                    }}
                  />
                  <div
                    className="
                      me-4 min-w-40 pt-3 text-caption

                      [&_a]:text-foreground-primary [&_a]:focus-outline

                      [&_a:hover]:text-foreground-primary-variant
                      [&_a:hover]:underline
                    "
                    dangerouslySetInnerHTML={{ __html: labels.needHelp }}
                  />
                </div>
              </div>
            </div>
            <Button
              className="
                cursor-pointer rounded-xl bg-background-primary py-3
                text-foreground-on-primary
              "
              onClick={() => {
                setIsFullScreenOpen(false);
              }}
            >
              Continue
            </Button>
          </div>
        </FloatingOverlay>
      )}
    </>
  );
};
